import type { FunctionalComponent as FC } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import { AdaptivityProvider, AppRoot, ConfigProvider } from '@vkontakte/vkui';

import { PhoneProvider } from '../../context/PhoneContext';
import { VkCaptchaProvider } from '../../context/VkCaptchaContext';
import { WidgetProvider } from '../../context/WidgetContext';
import { useInitVkId } from '../../hooks/useInitVkId';
import { useWidgetParams } from '../../hooks/useWidgetParams';
import type { WidgetTheme } from '../../types/theme';
import type { TokenManagerData } from '../../types/token-manager';
import { getThemeConfig, tokenManager } from '../../utils';
import '@vkontakte/vkui/dist/components.css';

import { WebSocketWrapper } from '../WebSocketWrapper';
import { Widget } from '../Widget';

import './themes.css';
import '@vkontakte/vkui-tokens/themes/vkBase/cssVars/declarations/onlyVariables.css';

interface Props {
  widgetTitle: string;
  companyLogoSrc?: string;
  widgetUuid?: string;
  widgetTheme?: WidgetTheme;
  entryPointGateway?: string;
  requirePhone?: boolean;
  panelOffsetTop?: number;
  hideTriggerButton?: boolean;
  widgetLocale?: string;
}

export const WidgetWrapper: FC<Props> = ({
  widgetTitle,
  companyLogoSrc,
  widgetUuid,
  entryPointGateway,
  requirePhone,
  widgetTheme = 'default',
  panelOffsetTop = 0,
  hideTriggerButton,
  widgetLocale,
}) => {
  const widgetParams = useWidgetParams({
    widgetTitle,
    companyLogoSrc,
    widgetUuid,
    widgetTheme,
    entryPointGateway,
    requirePhone,
    panelOffsetTop,
    hideTriggerButton,
    widgetLocale,
  });

  const {
    widgetUuid: widgetUuidParam,
    widgetTheme: widgetThemeParam,
    panelOffsetTop: panelOffsetTopParam,
  } = widgetParams;

  useInitVkId(widgetUuidParam);

  const themeConfig = getThemeConfig(widgetThemeParam);

  const [token, setToken] = useState(tokenManager.getToken());

  useEffect(() => {
    const callback = (data?: TokenManagerData) => {
      if (data?.token) {
        setToken(data.token);
      }
    };

    tokenManager.subscribe(callback);

    return () => tokenManager.unsubscribe(callback);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--vks-support-widget-panel-offset-top', panelOffsetTopParam + 'px');
  }, [panelOffsetTopParam]);

  return (
    <WidgetProvider {...widgetParams}>
      <PhoneProvider>
        <VkCaptchaProvider>
          <WebSocketWrapper token={token}>
            <ConfigProvider colorScheme="light" tokensClassNames={themeConfig}>
              <AdaptivityProvider>
                <AppRoot mode="embedded" disableParentTransformForPositionFixedElements>
                  <Widget />
                </AppRoot>
              </AdaptivityProvider>
            </ConfigProvider>
          </WebSocketWrapper>
        </VkCaptchaProvider>
      </PhoneProvider>
    </WidgetProvider>
  );
};
